import DOMPurify from 'dompurify';
import { getAdminEmailAttachmentDownloadUrl } from '../../services/adminService';
import EmailAvatar from './EmailAvatar';
import EmailRichTextEditor from './EmailRichTextEditor';
import { formatAddress, formatAddressList, formatDate, formatMailboxAddress } from './emailUtils';

function EmailDetail({
  onArchive,
  onReply,
  onToggleRead,
  replyBody,
  replyCc,
  selectedEmail,
  setReplyBody,
  setReplyCc,
  submitting,
  unsubscribes = [],
}) {
  if (!selectedEmail) {
    return (
      <div className="admin-panel admin-email-detail">
        <p className="admin-empty">Select an email or open compose.</p>
      </div>
    );
  }

  const sanitizedHtml = selectedEmail.html
    ? DOMPurify.sanitize(selectedEmail.html, { ADD_ATTR: ['target', 'rel'] }).replace(
        /<a\s/gi,
        '<a target="_blank" rel="noopener noreferrer" '
      )
    : '';
  const replySenderAddress =
    selectedEmail.direction === 'inbound'
      ? selectedEmail.recipientAddress || formatMailboxAddress(selectedEmail)
      : selectedEmail.from?.email || formatMailboxAddress(selectedEmail);
  const replyTarget =
    selectedEmail.direction === 'inbound' ? selectedEmail.from?.email : selectedEmail.to?.[0]?.email;
  const unsubscribedSet = new Set(
    (unsubscribes || []).map((record) => String(record.normalizedEmail || record.email || '').trim().toLowerCase())
  );
  const replyTargetUnsubscribed = unsubscribedSet.has(String(replyTarget || '').trim().toLowerCase());
  const factorStreetRetired =
    selectedEmail.service === 'factorstreet' || selectedEmail.domain === 'factorstreet.com';
  const updateReplyBody = (html) => {
    setReplyBody(html);
  };

  return (
    <div className="admin-panel admin-email-detail">
      <div className="admin-page__header--split">
        <div>
          <p className="eyebrow">{selectedEmail.folder}</p>
          <h2>{selectedEmail.subject}</h2>
        </div>
        <div className="admin-bulk-actions">
          <button onClick={onToggleRead} type="button">
            Mark {selectedEmail.readStatus === 'read' ? 'Unread' : 'Read'}
          </button>
          <button onClick={onArchive} type="button">
            Archive
          </button>
        </div>
      </div>
      <div className="lead-detail-grid">
        <span>From</span>
        <span className="admin-email-identity">
          <EmailAvatar address={selectedEmail.from} size="sm" />
          <span>{formatAddress(selectedEmail.from)}</span>
        </span>
        <span>To</span>
        <span>{formatAddressList(selectedEmail.to)}</span>
        <span>CC</span>
        <span>{formatAddressList(selectedEmail.cc)}</span>
        <span>Date</span>
        <span>{formatDate(selectedEmail.sentAt || selectedEmail.receivedAt || selectedEmail.createdAt)}</span>
        <span>Mailbox</span>
        <span className="admin-email-badge">{formatMailboxAddress(selectedEmail)}</span>
        <span>Status</span>
        <span>
          {selectedEmail.status} / {selectedEmail.readStatus}
        </span>
      </div>
      {selectedEmail.attachments?.length ? (
        <div className="admin-email-attachments">
          <h3>Attachments</h3>
          {selectedEmail.attachments.map((attachment, index) =>
            attachment.downloadPath ? (
              <a
                download={attachment.filename || 'attachment'}
                href={getAdminEmailAttachmentDownloadUrl(attachment.downloadPath)}
                key={`${attachment.filename}-${index}`}
                rel="noopener noreferrer"
                target="_blank"
              >
                <span>{attachment.filename}</span>
                <span>{attachment.contentType || 'file'}</span>
              </a>
            ) : (
              <span className="admin-email-attachment" key={`${attachment.filename}-${index}`}>
                <span>{attachment.filename}</span>
                <span>{attachment.contentType || 'file'}</span>
              </span>
            )
          )}
        </div>
      ) : null}
      {selectedEmail.emailError ? <p className="admin-email-warning">{selectedEmail.emailError}</p> : null}
      <div className="admin-email-body">
        {sanitizedHtml ? (
          <div dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />
        ) : (
          <p>{selectedEmail.text || 'No message body available.'}</p>
        )}
      </div>
      {factorStreetRetired ? (
        <div className="admin-email-reply">
          <h3>FactorStreet Reply moved</h3>
          <p>Reply from the AdminFactor Email desk. AWS SES is the only FactorStreet human-mail provider.</p>
          <a
            className="btn btn--primary"
            href="https://adminfactor.com/sites/1997f354-5920-4061-9a26-63b46a9c1d85/email"
            rel="noreferrer"
            target="_blank"
          >
            Open AdminFactor Email
          </a>
        </div>
      ) : (
      <form className="admin-email-reply" onSubmit={onReply}>
        <div className="admin-email-reply__header">
          <h3>Reply</h3>
          <span className="admin-email-identity">
            <EmailAvatar address={replySenderAddress} size="sm" />
            <span>{replySenderAddress}</span>
          </span>
        </div>
        {replyTargetUnsubscribed ? (
          <p className="admin-email-warning">
            This recipient is unsubscribed. Replies are allowed for active conversations.
          </p>
        ) : null}
        <label>
          CC
          <input onChange={(event) => setReplyCc(event.target.value)} value={replyCc} />
        </label>
        <EmailRichTextEditor ariaLabel="Reply body editor" value={replyBody} onChange={updateReplyBody} />
        <button className="btn btn--primary" disabled={submitting || !replyBody.trim()} type="submit">
          Send Reply
        </button>
      </form>
      )}
    </div>
  );
}

export default EmailDetail;
